import { ITranslation } from "@shared";

import { DutyScheduleColumnResponse } from "@/api";
import { DutyScheduleTableColumn } from "@/pages/duty-schedule/pages/duty-schedule-detail/components/table-layout/DutyScheduleTableLayout";
import {
  COUNSELOR_COLUMN_WIDTH,
  DutyScheduleCellHighlight,
} from "@/pages/duty-schedule/pages/duty-schedule-detail/components/table-layout/DutyScheduleTableLayout.styles";
import {
  DutyScheduleOverviewRow,
  TOTALS_KEY,
  UNFILLED_KEY,
} from "@/pages/duty-schedule/pages/duty-schedule-detail/pages/counselor-overview/DutyScheduleCounselorOverview.utils";

import { DUTY_COLUMN_WIDTH, SUM_COLUMN_WIDTH } from "./DutyScheduleCounselorOverviewTable.styles";

export function counselorColumnLabel(row: DutyScheduleOverviewRow, t: ITranslation): string {
  if (row.key === UNFILLED_KEY) {
    return t("dutySchedule.detail.dataEntry.cellUnoccupied");
  }
  if (row.key === TOTALS_KEY) {
    return t("dutySchedule.detail.counselorOverview.totalsRow");
  }
  return row.counselor == null
    ? row.userCode
    : t("dutySchedule.detail.counselorOverview.counselorLabel", {
        userCode: row.userCode,
        lastName: row.counselor.lastName,
        firstName: row.counselor.firstName,
      });
}

function rowHighlight(row: DutyScheduleOverviewRow): DutyScheduleCellHighlight {
  if (row.key === TOTALS_KEY) {
    return "totals";
  }
  if (row.key === UNFILLED_KEY) {
    return "muted";
  }
  return "none";
}

export function buildCounselorColumn(t: ITranslation): DutyScheduleTableColumn<DutyScheduleOverviewRow> {
  return {
    field: "counselor",
    headerName: t("dutySchedule.detail.counselorOverview.counselorColumn"),
    width: COUNSELOR_COLUMN_WIDTH,
    bold: true,
    // The one cell of the unfilled row that reads in italics — its duty/sum cells stay plain "muted".
    getHighlight: (row) => (row.key === UNFILLED_KEY ? "mutedItalic" : rowHighlight(row)),
    renderCell: (row) => counselorColumnLabel(row, t),
  };
}

export function buildDutyColumns(
  columns: DutyScheduleColumnResponse[]
): Array<DutyScheduleTableColumn<DutyScheduleOverviewRow>> {
  return columns.map((column) => ({
    field: column.id,
    headerName: column.label,
    width: DUTY_COLUMN_WIDTH,
    align: "right",
    bordered: true,
    getHighlight: rowHighlight,
    renderCell: (row) => {
      const count = row.counts.get(column.id) ?? 0;
      return count > 0 ? count : "";
    },
  }));
}

export function buildSumColumn(t: ITranslation): DutyScheduleTableColumn<DutyScheduleOverviewRow> {
  return {
    field: "__sum__",
    headerName: t("dutySchedule.detail.counselorOverview.sumColumn"),
    width: SUM_COLUMN_WIDTH,
    align: "right",
    bordered: true,
    bold: true,
    // Unlike the duty columns, the sum column is always tinted — "muted" doubles as the plain header
    // tint here for every non-totals row, including the unfilled row.
    getHighlight: (row) => (row.key === TOTALS_KEY ? "totals" : "muted"),
    renderCell: (row) => (row.total > 0 ? row.total : ""),
  };
}
